import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useQueues } from '../../lib/api/hooks-tms.js';
import { QueueVolumeChart } from '../../components/charts/Charts.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, Stat, StatGroup,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration, formatNumber } from '../../lib/format.js';

/**
 * Queue performance: every queue on one page, and one queue in depth.
 *
 * Deliberately not the live wallboard. A wallboard answers "what is happening
 * now" and is read standing up; this answers "did we answer the phone last
 * week", and every figure comes from the queue_events history rather than from
 * a counter — which is the only reason a report for last Tuesday exists at all.
 *
 * It opens on a row per queue with its headline figures, because the first question is which
 * queue needs attention, and a page that made you pick one from a dropdown before it showed you
 * anything answered that question by asking it back (owner, 23 September 2026). Clicking a row
 * opens that queue in full, where the page runs in the order the questions get asked: did we
 * answer them, how long did they wait, why did the rest leave, and who took the calls.
 */

interface Report {
  window: { from: string; to: string };
  queues: Array<{ queue_key: string; name: string; events: number }>;
  totals: {
    total: number; answered: number; abandoned: number;
    avg_wait_answered_sec: number; avg_wait_abandoned_sec: number;
    longest_wait_sec: number; answer_rate_pct: number;
  };
  service_level: Array<{ band: string; answered: number; abandoned: number }>;
  entry_positions: Array<{ position: number; callers: number }>;
  abandon_reasons: Array<{
    cause: string; cancel_reason: string; calls: number; avg_wait_sec: number;
  }>;
  agents: Array<{
    agent: string; calls_taken: number; offers: number;
    talk_sec: number; avg_talk_sec: number;
  }>;
  series: Array<{ at: string; answered: number; abandoned: number }>;
}

/** Bands come back grouped, so the order has to be imposed here. */
const BANDS = ['0-5', '6-10', '11-20', '21-30', '31-60', '60+', 'unknown'] as const;
const BAND_LABEL: Record<string, string> = {
  '0-5': 'under 5s', '6-10': '6–10s', '11-20': '11–20s',
  '21-30': '21–30s', '31-60': '31–60s', '60+': 'over a minute',
  unknown: 'not recorded',
};

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
] as const;
type RangeKey = typeof RANGES[number]['key'];

function rangeBounds(key: RangeKey): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString();
  if (key === 'today') {
    // Midnight in the BROWSER's zone, not the tenant's. Right for everyone on
    // SAST, and an hour out for an operator reviewing a Windhoek account from
    // elsewhere -- the same simplification the call history makes, and worth
    // replacing in both places at once when tenant timezone lands.
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to };
  }
  const days = key === '24h' ? 1 : key === '7d' ? 7 : 30;
  return { from: new Date(now.getTime() - days * 86400_000).toISOString(), to };
}

/**
 * Hourly buckets rolled up to days once the window is long enough.
 *
 * The endpoint always returns hours, because an hour is the smallest grain
 * worth asking the database for. 720 bars for a month is not a chart, though,
 * so the display grain is chosen here — where the width of the window is known.
 */
function bucket(series: Report['series'], key: RangeKey) {
  const daily = key === '7d' || key === '30d';
  if (!daily) {
    return series.map((p) => ({
      label: new Date(p.at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }),
      answered: p.answered, abandoned: p.abandoned,
    }));
  }
  const byDay = new Map<string, { answered: number; abandoned: number }>();
  for (const p of series) {
    const d = p.at.slice(0, 10);
    const e = byDay.get(d) ?? { answered: 0, abandoned: 0 };
    e.answered += p.answered;
    e.abandoned += p.abandoned;
    byDay.set(d, e);
  }
  return [...byDay.entries()].map(([day, v]) => ({
    label: new Date(day + 'T00:00:00Z').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }),
    answered: v.answered, abandoned: v.abandoned,
  }));
}

const pct = (n: number, d: number): number =>
  d > 0 ? Math.round((n / d) * 1000) / 10 : 0;

/** A queue's headline figures, for the row that stands for it. */
interface Summary {
  window: { from: string; to: string };
  target_sec: number;
  queues: Array<{
    queue_key: string | null; name: string; queue_id: string | null;
    offered: number; answered: number; abandoned: number;
    avg_wait_answered_sec: number; longest_wait_sec: number;
    answer_rate_pct: number | null; service_level_pct: number | null;
  }>;
}

export function QueueReportTab({ tenantId }: { tenantId: string }) {
  const [range, setRange] = useState<RangeKey>('7d');
  /** The queue being looked at in full, or null on the list. */
  const [open, setOpen] = useState<{ key: string; name: string } | null>(null);
  const queueKey = open?.key ?? '';
  const { from, to } = useMemo(() => rangeBounds(range), [range]);

  /* Only to tell "no queues exist" apart from "no calls arrived" -- two very
     different things to be told when a report comes back empty. */
  const configured = useQueues(tenantId);

  const summary = useQuery({
    queryKey: ['queue-summary', tenantId, from, to],
    queryFn: () => api.get<Summary>(
      `/v1/tenants/${tenantId}/queue-report/summary?from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}`),
    enabled: !!tenantId && !open,
  });

  const q = useQuery({
    queryKey: ['queue-report', tenantId, from, to, queueKey],
    queryFn: () => api.get<Report>(
      `/v1/tenants/${tenantId}/queue-report?from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}`
      + (queueKey ? `&queue_key=${encodeURIComponent(queueKey)}` : '')),
    // Only for the queue being looked at: the list has its own, cheaper, query.
    enabled: !!tenantId && !!open,
  });

  const t = q.data?.totals;
  const offered = t?.total ?? 0;

  const bands = useMemo(() => {
    const m = new Map(q.data?.service_level.map((b) => [b.band, b]) ?? []);
    return BANDS.map((b) => ({
      band: b as string,
      answered: m.get(b)?.answered ?? 0,
      abandoned: m.get(b)?.abandoned ?? 0,
    })).filter((b) => b.answered + b.abandoned > 0);
  }, [q.data]);

  /**
   * Cumulative answered-within, which is what a service level actually is.
   *
   * "80% in 20 seconds" is a running total, not the size of one band, and
   * reading it off separate rows is arithmetic nobody should have to do while
   * being asked whether the target was met.
   */
  const cumulative = useMemo(() => {
    let run = 0;
    return bands.map((b) => {
      run += b.answered;
      return { band: b.band, within: pct(run, t?.answered ?? 0) };
    });
  }, [bands, t]);

  const series = useMemo(() => bucket(q.data?.series ?? [], range), [q.data, range]);

  /** Three offers is the fewest that can distinguish a pattern from a coincidence. */
  const worstAgent = useMemo(() => {
    return (q.data?.agents ?? [])
      .filter((a) => a.offers >= 3)
      .map((a) => ({ ...a, rate: pct(a.calls_taken, a.offers) }))
      .sort((a, b) => a.rate - b.rate)[0];
  }, [q.data]);

  if (q.error) return <ErrorBanner error={q.error} />;

  const noQueues = !configured.isLoading && (configured.data?.data.length ?? 0) === 0;

  return (
    <>
      <div className="arc-page-head">
        <div>
          {open ? (
            <>
              <button type="button" className="arc-crumb" onClick={() => setOpen(null)}>
                <Icon name="arrowLeft" size={15} /> All queues
              </button>
              <h1 className="arc-h1" style={{ marginTop: 6 }}>{open.name}</h1>
            </>
          ) : (
            <h1 className="arc-h1">Queue performance</h1>
          )}
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Answered, abandoned and waiting times, from the queue event history.
          </p>
        </div>
      </div>

      {noQueues ? (
        <div className="arc-card">
          <Empty icon="queue" title="No queues configured"
            hint="Create a queue under Call routing before there is anything to report on." />
        </div>
      ) : (
        <>
          <div className="arc-card" style={{ marginBottom: 14 }}>
            <div className="arc-toolbar">
              <select className="arc-select" style={{ width: 'auto' }}
                value={range} onChange={(e) => setRange(e.target.value as RangeKey)}
                aria-label="Reporting window">
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <div className="arc-spacer" />
              <span className="arc-subtle" style={{ fontSize: 12.5 }}>
                {open
                  ? (q.isLoading ? 'loading…' : `${formatNumber(offered)} calls offered`)
                  : (summary.isLoading ? 'loading…'
                    : `${formatNumber((summary.data?.queues ?? []).reduce((n, x) => n + x.offered, 0))} calls offered`)}
              </span>
            </div>
          </div>

          {!open && (
            summary.error ? <ErrorBanner error={summary.error} />
              : summary.isLoading ? <div className="arc-skeleton" style={{ height: 120 }} />
                : <QueueList data={summary.data} onOpen={setOpen} />
          )}

          {open && (offered === 0 && !q.isLoading ? (
            <div className="arc-card">
              <Empty icon="queue" title="No queue activity in this window"
                hint="Events are written as callers enter and leave a queue. Widen the window, or check that calls are reaching the queue at all." />
            </div>
          ) : (
            <>
              <StatGroup>
                <Stat bare label="Offered" value={formatNumber(offered)}
                  sub="callers who entered" />
                <Stat bare label="Answered" value={`${t?.answer_rate_pct ?? 0}%`}
                  tone={(t?.answer_rate_pct ?? 0) >= 90 ? 'ok'
                    : (t?.answer_rate_pct ?? 0) >= 75 ? 'warn' : 'danger'}
                  sub={`${formatNumber(t?.answered ?? 0)} calls`} />
                <Stat bare label="Abandoned" value={formatNumber(t?.abandoned ?? 0)}
                  sub={`${pct(t?.abandoned ?? 0, offered)}% gave up`} />
                <Stat bare label="Average wait" value={formatDuration(t?.avg_wait_answered_sec ?? 0)}
                  sub="before being answered" />
                {/* The longest wait is the complaint that reaches the account
                    manager. An average never surfaces it. */}
                <Stat bare label="Longest wait" value={formatDuration(t?.longest_wait_sec ?? 0)}
                  tone={(t?.longest_wait_sec ?? 0) > 120 ? 'warn' : undefined}
                  sub="worst single caller" />
              </StatGroup>

              {t && t.abandoned > 0 && (
                <p className="arc-muted" style={{ fontSize: 12.5, margin: '10px 2px 14px' }}>
                  {formatNumber(t.abandoned)} caller{t.abandoned === 1 ? '' : 's'} hung up after
                  waiting {formatDuration(t.avg_wait_abandoned_sec)} on average
                  {t.avg_wait_abandoned_sec < t.avg_wait_answered_sec
                    ? ' — less than the calls that were answered, so they were giving up early rather than being kept too long.'
                    : ' — longer than the calls that were answered, so they were waiting behind something.'}
                </p>
              )}

              <div className="arc-card" style={{ marginBottom: 14 }}>
                <div className="arc-card-head">
                  <h2 className="arc-h2">Answered and abandoned over time</h2>
                  <span className="arc-subtle">
                    {range === '7d' || range === '30d' ? 'by day' : 'by hour'}
                  </span>
                </div>
                <div className="arc-card-body">
                  <QueueVolumeChart data={series} />
                </div>
              </div>

              <div className="arc-grid"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
                <div className="arc-card">
                  <div className="arc-card-head">
                    <h2 className="arc-h2">How long callers waited</h2>
                    <span className="arc-subtle">service level</span>
                  </div>
                  <div className="arc-table-wrap">
                    <table className="arc-table">
                      <thead>
                        <tr>
                          <th>Wait</th>
                          <th className="arc-td-num">Answered</th>
                          <th className="arc-td-num">Gave up</th>
                          <th className="arc-td-num">Answered within</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bands.map((b, i) => (
                          <tr key={b.band}>
                            <td>{BAND_LABEL[b.band] ?? b.band}</td>
                            <td className="arc-td-num arc-muted">{formatNumber(b.answered)}</td>
                            <td className="arc-td-num">
                              {b.abandoned > 0
                                ? <span style={{ color: 'var(--arc-danger)' }}>
                                    {formatNumber(b.abandoned)}
                                  </span>
                                : <span className="arc-subtle">—</span>}
                            </td>
                            <td className="arc-td-num" style={{ fontWeight: 600 }}>
                              {b.band === 'unknown'
                                ? <span className="arc-subtle">—</span>
                                : `${cumulative[i]?.within ?? 0}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="arc-card">
                  <div className="arc-card-head">
                    <h2 className="arc-h2">Why callers left</h2>
                    <span className="arc-subtle">unanswered only</span>
                  </div>
                  {(q.data?.abandon_reasons.length ?? 0) === 0 ? (
                    <Empty icon="check" title="Nobody gave up"
                      hint="Every caller in this window reached an agent." />
                  ) : (
                    <div className="arc-table-wrap">
                      <table className="arc-table">
                        <thead>
                          <tr>
                            <th>Outcome</th>
                            <th className="arc-td-num">Calls</th>
                            <th className="arc-td-num">Avg wait</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q.data?.abandon_reasons.map((r) => (
                            <tr key={`${r.cause}|${r.cancel_reason}`}>
                              <td>
                                <div className="arc-stack">
                                  <span style={{ fontWeight: 550 }}>{r.cause}</span>
                                  {r.cancel_reason && (
                                    <span className="arc-subtle" style={{ fontSize: 11 }}>
                                      {r.cancel_reason}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="arc-td-num arc-muted">{formatNumber(r.calls)}</td>
                              <td className="arc-td-num arc-muted">
                                {formatDuration(r.avg_wait_sec)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="arc-card" style={{ marginTop: 14 }}>
                <div className="arc-card-head">
                  <h2 className="arc-h2">Agents</h2>
                  <span className="arc-subtle">offers, answers and talk time</span>
                </div>

                {worstAgent && worstAgent.rate < 60 && (
                  <div className="arc-banner arc-banner-warn" style={{ margin: '0 14px 12px' }}>
                    <span>
                      {worstAgent.agent} answered {worstAgent.rate}% of the calls offered to
                      them — a phone that rings out sends the caller back into the queue
                      behind everyone else.
                    </span>
                  </div>
                )}

                <div className="arc-table-wrap">
                  <table className="arc-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th className="arc-td-num">Offered</th>
                        <th className="arc-td-num">Taken</th>
                        <th className="arc-td-num">Answer rate</th>
                        <th className="arc-td-num">Talk time</th>
                        <th className="arc-td-num">Avg call</th>
                      </tr>
                    </thead>
                    {q.isLoading ? <TableSkeleton cols={6} /> : (
                      <tbody>
                        {q.data?.agents.map((a) => {
                          const rate = pct(a.calls_taken, a.offers);
                          return (
                            <tr key={a.agent}>
                              <td className="arc-mono">{a.agent}</td>
                              <td className="arc-td-num arc-muted">{formatNumber(a.offers)}</td>
                              <td className="arc-td-num" style={{ fontWeight: 600 }}>
                                {formatNumber(a.calls_taken)}
                              </td>
                              <td className="arc-td-num">
                                {a.offers > 0
                                  ? <Badge tone={rate >= 85 ? 'ok' : rate >= 60 ? 'warn' : 'danger'}>
                                      {rate}%
                                    </Badge>
                                  : <span className="arc-subtle">—</span>}
                              </td>
                              <td className="arc-td-num arc-muted">{formatDuration(a.talk_sec)}</td>
                              <td className="arc-td-num arc-muted">{formatDuration(a.avg_talk_sec)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    )}
                  </table>
                </div>
                {!q.isLoading && (q.data?.agents.length ?? 0) === 0 && (
                  <Empty icon="users" title="No agent activity"
                    hint="Calls were offered to the queue but no agent was rung." />
                )}
              </div>

              {(q.data?.entry_positions.length ?? 0) > 0 && (
                <div className="arc-card" style={{ marginTop: 14 }}>
                  <div className="arc-card-head">
                    <h2 className="arc-h2">Where callers landed</h2>
                    <span className="arc-subtle">position on arrival</span>
                  </div>
                  <div className="arc-card-body">
                    {/* Position 1 means the queue was empty when they called. The
                        tail is what a caller hears as "you are number six", and it
                        is the number to watch when deciding on staffing. */}
                    <EntryPositions rows={q.data!.entry_positions} />
                  </div>
                </div>
              )}
            </>
          ))}
        </>
      )}
    </>
  );
}

/**
 * The landing page: one row per queue, its figures as tiles, click to open it.
 *
 * Tone on the answer rate and the longest wait, because the point of the page is to show which
 * queue to look at without reading every number on it.
 */
function QueueList({ data, onOpen }: {
  data: Summary | undefined;
  onOpen: (q: { key: string; name: string }) => void;
}) {
  const queues = data?.queues ?? [];
  if (queues.length === 0) {
    return (
      <div className="arc-card">
        <Empty icon="queue" title="No queue activity in this window"
          hint="Events are written as callers enter and leave a queue. Widen the window, or check that calls are reaching the queue at all." />
      </div>
    );
  }
  return (
    <div className="arc-stack" style={{ gap: 10 }}>
      {queues.map((qq) => {
        const rate = qq.answer_rate_pct;
        const idle = qq.offered === 0;
        const row = (
          <>
            <div className="arc-qrow-name">
              <strong>{qq.name}</strong>
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                {idle ? 'no calls in this window' : `${formatNumber(qq.offered)} offered`}
              </span>
            </div>
            <div className="arc-qrow-tiles">
              <Tile label="Answered" value={rate === null ? '—' : `${rate}%`}
                sub={idle ? undefined : `${formatNumber(qq.answered)} calls`}
                tone={rate === null ? undefined : rate >= 90 ? 'ok' : rate >= 75 ? 'warn' : 'danger'} />
              <Tile label={`In ${data?.target_sec ?? 20}s`}
                value={qq.service_level_pct === null ? '—' : `${qq.service_level_pct}%`}
                sub="service level" />
              <Tile label="Abandoned" value={formatNumber(qq.abandoned)}
                sub={idle ? undefined : `${pct(qq.abandoned, qq.offered)}% gave up`} />
              <Tile label="Avg wait" value={idle ? '—' : formatDuration(qq.avg_wait_answered_sec)}
                sub="before answer" />
              <Tile label="Longest" value={idle ? '—' : formatDuration(qq.longest_wait_sec)}
                tone={qq.longest_wait_sec > 120 ? 'warn' : undefined} sub="worst caller" />
            </div>
          </>
        );
        // A queue with no events has no key to report on: nothing to drill into.
        return qq.queue_key
          ? (
            <button key={qq.queue_key} type="button" className="arc-card arc-qrow"
              onClick={() => onOpen({ key: qq.queue_key!, name: qq.name })}>
              {row}
              <Icon name="chevronRight" size={16} />
            </button>
          )
          : <div key={qq.name} className="arc-card arc-qrow" data-idle="true">{row}</div>;
      })}
    </div>
  );
}

/** One figure in a queue's row. Smaller than a Stat, which is a card's worth of space. */
function Tile({ label, value, sub, tone }: {
  label: string; value: string; sub?: string | undefined;
  tone?: 'ok' | 'warn' | 'danger' | undefined;
}) {
  return (
    <div className="arc-qtile">
      <div className="arc-stat-label">{label}</div>
      <div className="arc-qtile-value" style={tone ? { color: `var(--arc-${tone})` } : undefined}>{value}</div>
      {sub && <div className="arc-qtile-sub">{sub}</div>}
    </div>
  );
}

function EntryPositions({ rows }: { rows: Report['entry_positions'] }) {
  const max = Math.max(1, ...rows.map((r) => r.callers));
  const total = rows.reduce((n, p) => n + p.callers, 0);
  const straightThrough = rows.find((p) => p.position === 1)?.callers ?? 0;

  return (
    <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 84 }}>
        {rows.map((r) => (
          <div key={r.position}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 5, justifyContent: 'flex-end', height: '100%',
            }}
            title={`${formatNumber(r.callers)} callers arrived at position ${r.position}`}>
            <div style={{
              width: '100%',
              height: `${Math.max(2, (r.callers / max) * 62)}px`,
              borderRadius: 3,
              background: r.position === 1 ? 'var(--arc-ok)' : 'var(--arc-c1)',
            }} />
            <span className="arc-subtle" style={{ fontSize: 11 }}>{r.position}</span>
          </div>
        ))}
      </div>
      <p className="arc-muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
        {pct(straightThrough, total)}% arrived to an empty queue.
      </p>
    </>
  );
}
